import { toast } from 'sonner'
import axios from '@/lib/axios'
import { paths } from '@/routes/paths'
import { endpoints } from '@/lib/axios'
import { useState, useEffect } from 'react'
import { fError } from '@/utils/format-error'
import { useBoolean } from '@/hooks/use-boolean'
import { PaginateResponse } from '@/utils/paginate'
import { useRouter } from '@/routes/hooks/use-router'
import { useDebounceText } from '@/hooks/use-debounce-text'
import CustomPagination from '@/components/pagination/custom-pagination'
import ConfirmDialog from '@/components/share-components/confirm-dialog'
import LoadingSection from '@/components/loading-indicator/loading-section'
import {
  RoosterMatchStatus,
  type RoosterMatch,
} from '@/types/rooster-match.type'

import RoosterMatchTable from '../rooster-match-table'
import RoosterMatchDetailDialog from '../rooster-match-detail-dialog'
import RoosterMatchStatusDialog from '../rooster-match-status-dialog'
import RoosterMatchFilter, {
  type RoosterMatchFilters,
} from '../rooster-match-filter'

const PAGE_SIZE = 10

export default function RoosterMatchView() {
  const router = useRouter()
  const loading = useBoolean(true)
  const [matches, setMatches] = useState<RoosterMatch[]>([])
  const [pageMeta, setPageMeta] = useState({
    limit: PAGE_SIZE,
    page: 1,
    total: 0,
  })
  const [filters, setFilters] = useState<RoosterMatchFilters>({
    page: 1,
    limit: PAGE_SIZE,
    matchNumber: '',
    status: '',
    arenaId: '',
    orderBy: 'createdAt:desc',
  })

  // Debounced search value
  const debouncedSearch = useDebounceText(filters.matchNumber)

  // Detail dialog state
  const [detailDialog, setDetailDialog] = useState<{
    open: boolean
    match: RoosterMatch | null
  }>({
    open: false,
    match: null,
  })

  // Confirm dialog state
  const [confirmDialog, setConfirmDialog] = useState<{
    open: boolean
    match: RoosterMatch | null
    action: 'delete' | 'approve' | 'reject' | null
  }>({
    open: false,
    match: null,
    action: null,
  })

  // Status dialog state
  const [statusDialog, setStatusDialog] = useState<{
    open: boolean
    match: RoosterMatch | null
  }>({
    open: false,
    match: null,
  })

  // Loading state for operations
  const [actionLoading, setActionLoading] = useState(false)

  const fetchMatches = async () => {
    try {
      loading.onTrue()
      // Prepare API parameters (similar to shops-view.tsx pattern)
      const apiParams = {
        page: filters.page,
        limit: filters.limit,
        orderBy: filters.orderBy,
        ...(debouncedSearch && { matchNumber: debouncedSearch }),
        ...(filters.status && { status: filters.status }),
        ...(filters.arenaId && { arenaId: filters.arenaId }),
      }

      const { data } = await axios.get<PaginateResponse<RoosterMatch>>(
        endpoints.roosterMatches.list,
        {
          params: apiParams,
        },
      )

      setMatches(data.items)
      setPageMeta({ limit: data.limit, page: data.page, total: data.total })
    } catch (error) {
      console.error(error)
      toast.error(fError(error))
    } finally {
      loading.onFalse()
    }
  }

  useEffect(() => {
    fetchMatches()
  }, [
    debouncedSearch,
    filters.page,
    filters.limit,
    filters.status,
    filters.arenaId,
    filters.orderBy,
  ])

  const handleFiltersChange = (newFilters: Partial<RoosterMatchFilters>) => {
    setFilters((prev) => ({ ...prev, ...newFilters }))
  }

  const handleAddClick = () => {
    router.push(paths.roosterMatch)
  }

  const handleView = (match: RoosterMatch) => {
    setDetailDialog({
      open: true,
      match,
    })
  }

  const handleEdit = (match: RoosterMatch) => {
    setStatusDialog({ open: true, match })
  }

  const handleApprove = (match: RoosterMatch) => {
    setConfirmDialog({
      open: true,
      match,
      action: 'approve',
    })
  }

  const handleReject = (match: RoosterMatch) => {
    setConfirmDialog({
      open: true,
      match,
      action: 'reject',
    })
  }

  const handleDelete = (match: RoosterMatch) => {
    setConfirmDialog({
      open: true,
      match,
      action: 'delete',
    })
  }

  const handleConfirmAction = async () => {
    if (!confirmDialog.match || !confirmDialog.action || actionLoading) return

    try {
      setActionLoading(true)

      switch (confirmDialog.action) {
        case 'approve':
          await axios.patch(
            endpoints.roosterMatches.update(confirmDialog.match.id),
            { status: RoosterMatchStatus.APPROVED },
          )
          toast.success('อนุมัติการแข่งขันสำเร็จ')
          break
        case 'reject':
          await axios.patch(
            endpoints.roosterMatches.update(confirmDialog.match.id),
            { status: RoosterMatchStatus.REJECTED },
          )
          toast.success('ปฏิเสธการแข่งขันสำเร็จ')
          break
        case 'delete':
          await axios.delete(
            endpoints.roosterMatches.delete(confirmDialog.match.id),
          )
          toast.success('ลบการแข่งขันสำเร็จ')
          break
      }

      // Refresh the list
      fetchMatches()
    } catch (error) {
      console.error(error)
      toast.error(fError(error))
    } finally {
      setActionLoading(false)
      setConfirmDialog({ open: false, match: null, action: null })
    }
  }

  const handlePageChange = (page: number) => {
    setFilters({
      ...filters,
      page,
    })
  }

  const getConfirmDialogConfig = () => {
    if (!confirmDialog.match || !confirmDialog.action) return null

    const configs = {
      approve: {
        title: 'ยืนยันการอนุมัติ',
        description: `คุณต้องการอนุมัติการแข่งขัน #${confirmDialog.match.id.slice(-8)} หรือไม่?`,
        confirmText: 'อนุมัติ',
        variant: 'default' as const,
      },
      reject: {
        title: 'ยืนยันการปฏิเสธ',
        description: `คุณต้องการปฏิเสธการแข่งขัน #${confirmDialog.match.id.slice(-8)} หรือไม่?`,
        confirmText: 'ปฏิเสธ',
        variant: 'destructive' as const,
      },
      delete: {
        title: 'ยืนยันการลบ',
        description: `คุณต้องการลบการแข่งขัน #${confirmDialog.match.id.slice(-8)} หรือไม่? การดำเนินการนี้ไม่สามารถยกเลิกได้`,
        confirmText: 'ลบ',
        variant: 'destructive' as const,
      },
    }

    return configs[confirmDialog.action]
  }

  const dialogConfig = getConfirmDialogConfig()

  return (
    <div className="w-full space-y-6">
      {/* Filter Section */}
      <RoosterMatchFilter
        filters={filters}
        onFiltersChange={handleFiltersChange}
        onAddClick={handleAddClick}
      />

      <LoadingSection loading={loading.value}>
        <div className="space-y-6">
          {/* Table Section */}
          <RoosterMatchTable
            matches={matches}
            loading={loading.value}
            onView={handleView}
            onEdit={handleEdit}
            onApprove={handleApprove}
            onReject={handleReject}
            onDelete={handleDelete}
          />

          {/* Pagination */}
          {Math.ceil(pageMeta.total / pageMeta.limit) > 1 && (
            <CustomPagination
              currentPage={filters.page}
              totalPages={Math.ceil(pageMeta.total / pageMeta.limit)}
              onPageChange={handlePageChange}
            />
          )}
        </div>
      </LoadingSection>

      {/* Detail Dialog */}
      <RoosterMatchDetailDialog
        open={detailDialog.open}
        onOpenChange={(open) => {
          if (!open) {
            // Close dialog first
            setDetailDialog({ open: false, match: detailDialog.match })
            // Clear match state after dialog closes
            setTimeout(() => {
              setDetailDialog({ open: false, match: null })
            }, 150)
          } else {
            setDetailDialog({ open, match: detailDialog.match })
          }
        }}
        match={detailDialog.match}
      />

      {/* Status Dialog */}
      <RoosterMatchStatusDialog
        open={statusDialog.open}
        match={statusDialog.match}
        onOpenChange={(open) => {
          if (!open) {
            setStatusDialog({ open: false, match: statusDialog.match })
            setTimeout(() => setStatusDialog({ open: false, match: null }), 150)
          } else {
            setStatusDialog({ open, match: statusDialog.match })
          }
        }}
        onSaved={fetchMatches}
      />

      {/* Confirm Dialog */}
      {dialogConfig && (
        <ConfirmDialog
          open={confirmDialog.open}
          onOpenChange={(open) =>
            setConfirmDialog({ open, match: null, action: null })
          }
          title={dialogConfig.title}
          description={dialogConfig.description}
          confirmText={dialogConfig.confirmText}
          cancelText="ยกเลิก"
          variant={dialogConfig.variant}
          onConfirm={handleConfirmAction}
          loading={actionLoading}
        />
      )}
    </div>
  )
}
